import { useState, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Upload, Loader2, Check, X, FileText, Users } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const API = `${BACKEND_URL}/api`;

export default function BulkUploadButton({ onUploadComplete }) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results, setResults] = useState(null);
  const [autoCreatePlayers, setAutoCreatePlayers] = useState(true);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setResults(null);

    const allResults = [];
    const token = localStorage.getItem('token');

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('auto_create_players', autoCreatePlayers.toString());

        const response = await axios.post(
          `${API}/athletic-tests/bulk-upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        allResults.push({
          filename: file.name,
          success: true,
          ...response.data,
        });
      } catch (error) {
        allResults.push({
          filename: file.name,
          success: false,
          error: error.response?.data?.detail || 'Upload failed',
        });
      }
    }

    setResults(allResults);
    setUploading(false);

    // Show summary toast
    const totalUploaded = allResults.reduce(
      (sum, r) => sum + (r.uploaded_count || 0),
      0
    );
    const totalCreated = allResults.reduce(
      (sum, r) => sum + Object.keys(r.players_created || {}).length,
      0
    );
    const failedCount = allResults.filter((r) => !r.success).length;

    if (failedCount === 0) {
      toast.success(
        `Upload complete: ${totalUploaded} tests, ${totalCreated} new players created`
      );
    } else {
      toast.warning(
        `Upload complete with ${failedCount} error(s). ${totalUploaded} tests uploaded.`
      );
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Callback to refresh data
    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  return (
    <div className="relative">
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="h-10 px-5 font-bold uppercase tracking-wider text-sm bg-gradient-to-r from-emerald-500 to-green-500 hover:shadow-[0_0_20px_rgba(16,185,129,0.4)] text-white"
        data-testid="bulk-upload-button"
      >
        <Upload className="mr-2 h-4 w-4" />
        Bulk Upload CSV
      </Button>

      {isOpen && (
        <div className="absolute top-12 right-0 z-50 w-96 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-white">Bulk Upload Athletic Tests</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              className="h-8 w-8 text-zinc-400 hover:text-white"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs text-zinc-400 mb-3">
            Upload CSV files from Photon Sports or other athletic testing platforms.
            Supports CMJ, Sprint, 5-0-5 Agility, and more.
          </p>

          {/* Auto-create players toggle */}
          <div className="flex items-center gap-2 mb-4 p-2 bg-zinc-800/50 rounded">
            <input
              type="checkbox"
              id="autoCreatePlayers"
              checked={autoCreatePlayers}
              onChange={(e) => setAutoCreatePlayers(e.target.checked)}
              className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500 focus:ring-emerald-500"
            />
            <label htmlFor="autoCreatePlayers" className="text-sm text-zinc-300">
              <Users className="inline h-4 w-4 mr-1" />
              Auto-create new players from CSV
            </label>
          </div>

          {/* File input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            multiple
            onChange={handleFileSelect}
            className="hidden"
            id="csv-upload"
          />

          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white mb-3"
          >
            {uploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Select CSV Files
              </>
            )}
          </Button>

          {/* Results */}
          {results && results.length > 0 && (
            <div className="mt-3 max-h-64 overflow-y-auto">
              <h4 className="text-sm font-medium text-zinc-300 mb-2">Upload Results</h4>
              {results.map((result, idx) => (
                <div
                  key={idx}
                  className={`p-2 rounded mb-2 ${
                    result.success
                      ? 'bg-emerald-500/10 border border-emerald-500/30'
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    {result.success ? (
                      <Check className="h-4 w-4 text-emerald-400" />
                    ) : (
                      <X className="h-4 w-4 text-red-400" />
                    )}
                    <span className="text-sm font-medium text-white">
                      {result.filename}
                    </span>
                  </div>
                  {result.success ? (
                    <div className="text-xs text-zinc-400 mt-1 ml-6">
                      {result.uploaded_count} tests uploaded
                      {Object.keys(result.players_created || {}).length > 0 && (
                        <span className="text-emerald-400">
                          {' '}• {Object.keys(result.players_created).length} new players
                        </span>
                      )}
                      {result.duplicates_skipped > 0 && (
                        <span className="text-yellow-400">
                          {' '}• {result.duplicates_skipped} duplicates skipped
                        </span>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-red-400 mt-1 ml-6">
                      {result.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
